import {
  createFileRoute,
  useRouter,
  useNavigate,
} from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { useStats } from "@/lib/hooks/useApi";
import { generateTextShadow } from "@/lib/utils";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { t } from "@/lib/i18n";
import MosaicMask from "@/components/MosaicMask";
import { Check } from "lucide-react";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { usePostHog } from "posthog-js/react";
import { Spinner } from "@/components/ui/spinner";
import { DefaultButton } from "@/components/DefaultButton";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { LoginGate } from "@/components/LoginGate";

type SocialTaskKey = "twitterFollow" | "discordJoin" | "retweet";

type SocialTaskState = Record<SocialTaskKey, boolean>;

const SOCIAL_TASK_STORAGE_KEY = "cfl-social-tasks";

export const Route = createFileRoute("/(game)/gacha-task/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = usePrivy();
  const router = useRouter();
  const navigate = useNavigate();
  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");

  const [copied, setCopied] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isRestoringTasks, setIsRestoringTasks] = useState(true);
  const [socialTasks, setSocialTasks] = useState<SocialTaskState>({
    twitterFollow: false,
    discordJoin: false,
    retweet: false,
  });
  const [taskLoading, setTaskLoading] = useState<
    Record<SocialTaskKey, boolean>
  >({
    twitterFollow: false,
    discordJoin: false,
    retweet: false,
  });
  const taskTimeoutsRef = useRef<Record<SocialTaskKey, number | null>>({
    twitterFollow: null,
    discordJoin: null,
    retweet: null,
  });
  const prevRequiredTasksComplete = useRef(false);
  const posthog = usePostHog();

  const gachaPoint = statsData?.gachaCoin || 0;
  const refCode = statsData?.referralCode || "";

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsRestoringTasks(false);
      return;
    }

    try {
      const storedTasks = window.localStorage.getItem(SOCIAL_TASK_STORAGE_KEY);

      if (!storedTasks) {
        return;
      }

      const parsed = JSON.parse(storedTasks) as Partial<SocialTaskState>;
      setSocialTasks((prev) => {
        const next = {
          ...prev,
          ...parsed,
        };

        prevRequiredTasksComplete.current =
          next.twitterFollow && next.discordJoin && next.retweet;

        return next;
      });
    } catch (error) {
      console.error("Failed to read social tasks from storage:", error);
    } finally {
      setIsRestoringTasks(false);
    }
  }, []);

  useEffect(() => {
    const timeouts = taskTimeoutsRef.current;

    return () => {
      if (typeof window === "undefined") {
        return;
      }

      Object.values(timeouts).forEach((timeoutId) => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  useEffect(() => {
    const currentRequiredComplete =
      socialTasks.twitterFollow &&
      socialTasks.discordJoin &&
      socialTasks.retweet;

    if (isRestoringTasks || !posthog) {
      prevRequiredTasksComplete.current = currentRequiredComplete;
      return;
    }

    if (!prevRequiredTasksComplete.current && currentRequiredComplete) {
      posthog.capture("gacha_task_completed", {
        gachaPoint,
        refCode,
      });
    }

    prevRequiredTasksComplete.current = currentRequiredComplete;
  }, [
    gachaPoint,
    isRestoringTasks,
    posthog,
    refCode,
    socialTasks.discordJoin,
    socialTasks.retweet,
    socialTasks.twitterFollow,
  ]);

  const markTaskComplete = (task: SocialTaskKey) => {
    setSocialTasks((prev) => {
      if (prev[task]) {
        return prev;
      }

      const next = {
        ...prev,
        [task]: true,
      };

      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            SOCIAL_TASK_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch (error) {
          console.error("Failed to store social task progress:", error);
        }
      }

      return next;
    });
  };

  const handleSocialTaskClick = (task: SocialTaskKey, url: string) => {
    if (socialTasks[task] || taskLoading[task]) {
      return;
    }

    setTaskLoading((prev) => ({
      ...prev,
      [task]: true,
    }));

    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }

    if (typeof window !== "undefined") {
      const timeoutId = window.setTimeout(() => {
        markTaskComplete(task);
        setTaskLoading((prev) => ({
          ...prev,
          [task]: false,
        }));
        taskTimeoutsRef.current[task] = null;
      }, 5000);

      taskTimeoutsRef.current[task] = timeoutId;
    } else {
      markTaskComplete(task);
      setTaskLoading((prev) => ({
        ...prev,
        [task]: false,
      }));
    }
  };

  const socialTaskConfigs: ReadonlyArray<{
    key: SocialTaskKey;
    label: string;
    url: string;
    cta: string;
    optional?: boolean;
  }> = [
    {
      key: "twitterFollow",
      label: t("gachaTask.tasks.twitterFollow.label"),
      url: "https://x.com/intent/user?screen_name=cfldotfun",
      cta: t("gachaTask.tasks.twitterFollow.cta"),
    },
    {
      key: "discordJoin",
      label: t("gachaTask.tasks.discordJoin.label"),
      url: "https://discord.gg/GQuP7ekfF9",
      cta: t("gachaTask.tasks.discordJoin.cta"),
    },
    {
      key: "retweet",
      label: t("gachaTask.tasks.retweet.label"),
      url: "https://x.com/intent/retweet?tweet_id=1987897893001564358",
      cta: t("gachaTask.tasks.retweet.cta"),
    },
  ];

  const requiredTasksComplete =
    socialTasks.twitterFollow && socialTasks.discordJoin && socialTasks.retweet;

  const isLocked = !requiredTasksComplete;

  const handleCopy = async () => {
    try {
      const shareUrl = `${window.location.origin}/?ref=${refCode}`;

      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleInvite = async () => {
    try {
      const shareUrl = `${window.location.origin}/?ref=${refCode}`;

      if (navigator.share) {
        await navigator.share({
          text: "Come play Crypto Fantasy League with me! Get 1% cashback on every tournament you join",
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast("Share link copied to clipboard!");
      }
    } catch (error) {
      console.error("Failed to share referral:", error);
    }
  };

  const handleNavigateToLeaderboard = () => {
    setIsNavigating(true);
    router.navigate({ to: "/referrals" });
  };

  return (
    <div className="flex flex-col w-full relative overflow-hidden min-h-0 flex-1">
      {/* Back Button */}
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      {/* Main Content */}
      <div
        className="flex-1 flex flex-col items-center w-full"
        style={{
          backgroundImage:
            "url('https://cdn.navalabs.io/assets/bg-full_cJmDRn.png')",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Header */}
        <div className="w-full px-2 py-2 border-0 flex flex-col items-center mt-6">
          <span
            className="text-white font-black text-2xl text-center"
            style={{
              textShadow: generateTextShadow(1.5),
            }}
          >
            {t("gachaTask.gachaCoins")}
          </span>
        </div>

        {/* Gacha Machine Display */}
        <div className="w-full px-2 flex flex-col items-center">
          <div className="relative flex flex-col items-center">
            {/*<GachaMachine className="size-24 relative z-10" />*/}
            <div className="relative brown-container mb-2 h-8 w-16 rounded-lg z-0 flex items-center justify-center">
              <img
                src="https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png"
                alt={t("gachaTask.gachaMachine")}
                className="size-8 absolute -left-4"
              />
              <span className="text-white text-[1rem] font-bold text-center">
                {gachaPoint}
              </span>
            </div>
            <div className="w-[25%] sm:w-[40%] relative z-10 flex flex-col items-center justify-center">
              {/* {gachaPoint} */}
              <img
                src="https://cdn.navalabs.io/assets/gacha-machine_wdFBPO.png"
                alt={t("gachaTask.gachaMachine")}
              />
            </div>
          </div>
        </div>

        {/* Tasks Section */}
        <div className="relative bg-[#1D2A52] px-2 pb-4 flex flex-1 flex-col items-center z-10 w-full">
          <span
            className="text-white text-lg font-black text-center uppercase whitespace-nowrap -mt-2.5"
            style={{
              textShadow: generateTextShadow(1.25),
            }}
          >
            {t("gachaTask.completeTasksToUnlock")}
          </span>
          <span className="w-[90%] text-white/80 font-black text-base text-center mt-2">
            {t("gachaTask.referDescription")}
          </span>

          <div className="flex flex-col gap-3 mt-4 w-full">
            <div className="w-full indigo-container !border-4 flex flex-col">
              {socialTaskConfigs.map((task) => {
                const completed = socialTasks[task.key];
                const isLoading = taskLoading[task.key];

                return (
                  <div
                    key={task.key}
                    className="flex items-center justify-between gap-3 p-3"
                  >
                    <div className="flex flex-col text-left">
                      <span className="text-white text-sm font-extrabold text-shadow-black-sm -mt-1">
                        {task.label}
                      </span>
                      {task.optional ? (
                        <span className="text-emerald-700 text-xs font-bold mt-1">
                          Optional
                        </span>
                      ) : null}
                    </div>

                    <LoginGate unauthenticatedLabel={task.cta.toUpperCase()}>
                      <DefaultButton
                        variant="green"
                        onClick={() =>
                          handleSocialTaskClick(task.key, task.url)
                        }
                        disabled={completed || isLoading}
                        className={`h-8 !px-3`}
                      >
                        {isLoading ? (
                          <AnimatedWaiting
                            prefix={t("gachaTask.checking")}
                            className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase"
                          />
                        ) : completed ? (
                          <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                            <Check className="w-4 h-4" />
                            {t("gachaTask.completed")}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                            {task.cta}
                          </span>
                        )}
                      </DefaultButton>
                    </LoginGate>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Referral Code Section */}
          <div className="relative mt-auto w-full">
            <button
              type="button"
              onClick={handleCopy}
              disabled={isLocked}
              className="indigo-container relative flex w-full items-center justify-center rounded-md px-4 py-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span
                className="text-center text-base font-black tracking-wide text-white"
                style={{
                  textShadow: generateTextShadow(1),
                }}
              >
                {!copied ? (
                  refCode
                ) : (
                  <span className="inline-flex items-center gap-1.5 font-black tracking-normal text-white">
                    {t("gachaTask.copied")}
                    <svg
                      className="hover:fill-white"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_288_291)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0.166748 8.00001C0.166748 3.67377 3.67385 0.166672 8.00008 0.166672C12.3263 0.166672 15.8334 3.67377 15.8334 8.00001C15.8334 12.3262 12.3263 15.8333 8.00008 15.8333C3.67385 15.8333 0.166748 12.3262 0.166748 8.00001ZM7.52571 8.37447C7.46271 8.29891 7.37961 8.20021 7.27795 8.08157C7.02628 7.78784 6.66068 7.37161 6.20425 6.88077C5.83788 6.48684 5.22385 6.39267 4.79555 6.76537C4.60075 6.93484 4.38745 7.14554 4.19715 7.39021C3.90648 7.76394 3.97891 8.26427 4.25351 8.60457C5.15771 9.72521 6.03091 10.6403 6.65575 11.2562C7.22921 11.8215 8.13361 11.7755 8.63958 11.146C9.32018 10.2992 10.4079 8.85261 11.8549 6.63234C12.0584 6.32011 12.1257 5.88584 11.8782 5.54831C11.6223 5.19941 11.301 4.95794 11.0225 4.79684C10.5678 4.53381 10.0471 4.74611 9.77291 5.13537C9.04205 6.17314 8.44901 7.02887 8.03868 7.62524C7.83351 7.92344 7.67401 8.15677 7.56578 8.31564L7.52571 8.37447Z"
                          fill="#00D492"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_288_291">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                )}
              </span>

              <svg
                className={`${copied ? "hidden" : "ml-1 fill-white transition"} ${
                  isLocked ? "opacity-70" : ""
                }`}
                height="16"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_245_292)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.91675 0.208316C9.45654 0.202675 9.07892 0.571169 9.07329 1.03137C9.06763 1.49158 9.43612 1.86921 9.89633 1.87486C10.5007 1.88226 11.052 1.89523 11.5521 1.91185C12.012 1.92714 12.3973 1.56664 12.4126 1.10666C12.4279 0.646677 12.0674 0.261395 11.6075 0.246107C11.0955 0.229091 10.5326 0.215864 9.91675 0.208316ZM7.40867 1.87727C7.86887 1.87067 8.23658 1.49226 8.23 1.03208C8.22337 0.571885 7.84496 0.204177 7.38479 0.210776C6.76679 0.219637 6.20379 0.234221 5.69367 0.252515C5.23371 0.269009 4.87425 0.655235 4.89075 1.11518C4.90721 1.57512 5.29346 1.93461 5.75337 1.91811C6.25133 1.90025 6.80242 1.88596 7.40867 1.87727ZM14.2557 0.415867C13.8047 0.324115 13.3647 0.615344 13.273 1.06634C13.1812 1.51734 13.4725 1.95733 13.9235 2.04908C14.4056 2.14716 14.7735 2.48923 14.909 2.94915C15.0391 3.39062 15.5025 3.64302 15.944 3.51292C16.3854 3.38281 16.6378 2.91946 16.5077 2.478C16.1978 1.42634 15.3427 0.637023 14.2557 0.415867ZM3.44913 2.07736C3.89062 1.94734 4.14312 1.48404 4.0131 1.04256C3.88309 0.601065 3.41979 0.348565 2.9783 0.478581C1.90549 0.794519 1.10661 1.67735 0.903824 2.7939C0.821583 3.24674 1.122 3.68049 1.57483 3.76274C2.02767 3.84498 2.46142 3.54456 2.54367 3.09172C2.63393 2.59469 2.98069 2.21531 3.44913 2.07736ZM2.41823 5.25321C2.43473 4.79329 2.07524 4.40704 1.6153 4.39059C1.15536 4.37409 0.769132 4.73354 0.752638 5.1935C0.734346 5.70363 0.71976 6.26663 0.710899 6.88463C0.704301 7.34479 1.07201 7.72321 1.5322 7.72979C1.99239 7.73642 2.3708 7.36871 2.3774 6.9085C2.38609 6.30225 2.40038 5.75117 2.41823 5.25321ZM2.37498 9.39613C2.36934 8.93592 1.9917 8.56742 1.53149 8.57309C1.07129 8.57871 0.702797 8.95638 0.708438 9.41654C0.715987 10.0323 0.729215 10.5953 0.74623 11.1072C0.761519 11.5672 1.1468 11.9277 1.60679 11.9124C2.06677 11.8971 2.42727 11.5118 2.41198 11.0519C2.39536 10.5518 2.38239 10.0005 2.37498 9.39613ZM2.5492 13.4232C2.45745 12.9722 2.01747 12.681 1.56647 12.7727C1.11547 12.8645 0.824238 13.3045 0.915984 13.7555C1.13716 14.8427 1.9267 15.6978 2.9786 16.0076C3.42009 16.1377 3.8834 15.8852 4.01343 15.4437C4.14346 15.0022 3.89098 14.5389 3.44949 14.4089C2.98945 14.2734 2.64729 13.9054 2.5492 13.4232ZM5.03242 7.06692C4.95142 8.18067 4.87504 9.82879 4.87504 12.0833C4.87504 14.3377 4.95142 15.9858 5.03242 17.0996C5.13237 18.4744 6.19217 19.5343 7.56704 19.6342C8.68079 19.7152 10.3289 19.7916 12.5834 19.7916C14.8378 19.7916 16.486 19.7152 17.5997 19.6342C18.9745 19.5343 20.0344 18.4744 20.1343 17.0996C20.2153 15.9858 20.2917 14.3377 20.2917 12.0833C20.2917 9.82879 20.2153 8.18067 20.1343 7.06692C20.0344 5.69209 18.9745 4.63225 17.5997 4.53229C16.486 4.45134 14.8378 4.37492 12.5834 4.37492C10.3289 4.37492 8.68079 4.45129 7.56704 4.53229C6.19221 4.63225 5.13237 5.69204 5.03242 7.06692Z"
                  />
                </g>
              </svg>
            </button>

            {/* {!isLocked ? (
              <div className="mt-2 text-center text-xs text-white">
                <p>
                  {t("gachaTask.this")} <span className="font-bold">{t("gachaTask.refCode")}</span> {t("gachaTask.onlyValidOnThe")}{" "}
                  <a
                    href="https://cfl.fun"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-green-400 hover:text-green-600 hover:underline"
                  >
                    cfl.fun
                  </a>
                  {". "}
                  {t("gachaTask.headThereToClaim")}
                </p>
              </div>
            ) : null} */}

            <MosaicMask
              active={isLocked}
              columns={26}
              rows={10}
              className="z-10 rounded-md"
            />
          </div>
          {isLocked ? (
            <div className="mt-2 text-center text-sm text-white/80 px-4">
              <p>
                {/* Complete the required steps above to unlock your referral code. */}
                {t("gachaTask.completeRequiredSteps")}
              </p>
            </div>
          ) : null}

          {/* Share Button */}
          <div className="flex gap-2 mt-auto w-full">
            <DefaultButton
              onClick={handleInvite}
              variant="green"
              disabled={isLocked}
              className="!clamp-[h,12,14] w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="text-base text-white font-black">
                {t("gachaTask.shareRefCode")}
              </span>
            </DefaultButton>
          </div>

          {/* View Referrals Button */}
          <button
            onClick={handleNavigateToLeaderboard}
            disabled={isNavigating}
            className="text-gray-50 font-black text-xs cursor-pointer hover:text-gray-200 mt-4 mb-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 uppercase"
          >
            {isNavigating && <Spinner variant="sm" />}

            <span className="text-shadow-black-sm">
              {t("gachaTask.viewMyReferrals")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
